import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { ChevronDownIcon, ChevronUpIcon, InformationCircleIcon, ArrowPathIcon, SparklesIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { FolderOpen, Bolt, Cpu, CheckCircle, Plus } from 'lucide-react';

import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Switch } from '../components/ui/Switch';
import { Slider } from '../components/ui/Slider';
import { RootState } from '../store';
import { projectService } from '../services/projectService';
import { coreService } from '../services/coreService';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { Tooltip } from '../components/ui/Tooltip';
import { TagInput } from '../components/ui/TagInput';
import { useDrafts } from '../hooks/useDrafts';
import { SaveDraftModal } from '../components/ui/SaveDraftModal';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';


// Interface for Preferences
interface Preferences {
  brd: { enabled: boolean; value: number };
  prd: { enabled: boolean; value: number };
  uir: { enabled: boolean; value: number };
  nfr: { enabled: boolean; value: number };
}

export const ProjectCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { projects } = useSelector((state: RootState) => state.projects);

  const { drafts, saveDraft, deleteDraft } = useDrafts();
  const draftState = location.state?.draft;

  const isPro = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise';
  const projectLimit = isPro ? 10 : 3;
  const hasReachedProjectLimit = projects.length >= projectLimit;

  // Form State
  const [projectType, setProjectType] = useState<'TECH' | 'NON_TECH'>('TECH');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [technicalDetails, setTechnicalDetails] = useState('');
  const [techKeywords, setTechKeywords] = useState<string[]>([]);
  const [isBuilt, setIsBuilt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ name?: string; description?: string; technicalDetails?: string; techKeyword?: string }>({});
  const [isEnhancingDesc, setIsEnhancingDesc] = useState(false);
  const [isEnhancingTech, setIsEnhancingTech] = useState(false);
  const [isEnhancingKeywords, setIsEnhancingKeywords] = useState(false);
  const [isExtractingFile, setIsExtractingFile] = useState(false);

  const visionWordCount = description.trim().split(/\s+/).filter(Boolean).length;
  const isVisionReady = visionWordCount >= 500;


  // Preference State
  const [preferences, setPreferences] = useState<Preferences>({
    brd: { enabled: true, value: 15 },
    prd: { enabled: true, value: 15 },
    uir: { enabled: true, value: 15 },
    nfr: { enabled: true, value: 15 },
  });

  // Section Collapse State
  const [showBasicInfo, setShowBasicInfo] = useState(true);
  const [showPreferences, setShowPreferences] = useState(true);
  const [showMCP, setShowMCP] = useState(true);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [showTechModal, setShowTechModal] = useState(false);
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);

  const [isValidatingKeywords, setIsValidatingKeywords] = useState(false);

  const [draftId, setDraftId] = useState<string | undefined>(draftState?.id);
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  React.useEffect(() => {
    if (draftState) {
      setName(draftState.name || '');
      setDescription(draftState.description || '');
      setTechnicalDetails(draftState.technicalDetails || '');
      setTechKeywords(draftState.techKeywords || []);
      setIsBuilt(draftState.isBuilt || false);
      if (draftState.preferences) setPreferences(draftState.preferences);
    }
  }, [draftState]);

  const isDirty = React.useMemo(() => {
    return name.trim() !== '' || description.trim() !== '' || technicalDetails.trim() !== '' || techKeywords.length > 0;
  }, [name, description, technicalDetails, techKeywords]);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  // Auto-save helper — silently saves and navigates, used when draftId already exists
  const autoSaveAndNavigate = React.useCallback(async (url: string) => {
    try {
      const id = await saveDraft({ name, description, technicalDetails, techKeywords, isBuilt, preferences }, draftId);
      if (id) setDraftId(id);
      toast.success('Draft auto-saved ✓', { duration: 2000 });
    } catch {
      toast.error('Auto-save failed.');
    }
    if (url === '-1') navigate(-1);
    else navigate(url);
  }, [saveDraft, draftId, name, description, technicalDetails, techKeywords, isBuilt, preferences, navigate]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirty || isSubmitting) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      
      if (anchor && anchor.href) {
        const url = new URL(anchor.href);
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          e.preventDefault();
          e.stopPropagation();
          if (draftId) {
            // Already has a draft — auto-save silently
            autoSaveAndNavigate(url.pathname + url.search + url.hash);
          } else {
            setPendingUrl(url.pathname + url.search + url.hash);
            setShowSaveDraftModal(true);
          }
        }
      }
    };
    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, [isDirty, isSubmitting, draftId, autoSaveAndNavigate]);
  
  React.useEffect(() => {
    if (!isDirty || isSubmitting) return;
    window.history.pushState(null, '', window.location.pathname);
    const handlePopState = () => {
      if (draftId) {
        autoSaveAndNavigate('-1');
      } else {
        setPendingUrl('-1');
        setShowSaveDraftModal(true);
      }
      window.history.pushState(null, '', window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, isSubmitting, draftId, autoSaveAndNavigate]);

  const handleSaveDraft = async () => {
    try {
      const id = await saveDraft({
        name,
        description,
        technicalDetails,
        techKeywords,
        isBuilt,
        preferences
      }, draftId);
      if (id) setDraftId(id);
      toast.success('Draft saved successfully');
      setShowSaveDraftModal(false);
      if (pendingUrl === '-1') navigate(-1);
      else if (pendingUrl) navigate(pendingUrl);
    } catch (e) {
      toast.error('Failed to save draft. Please try again.');
      console.error(e);
    }
  };

  const handleDiscardDraft = async () => {
    try {
      if (draftId) await deleteDraft(draftId);
      setShowSaveDraftModal(false);
      if (pendingUrl === '-1') navigate(-1);
      else if (pendingUrl) navigate(pendingUrl);
    } catch (e) {
      toast.error('Failed to discard draft.');
      console.error(e);
    }
  };

  // Manual Save Draft button handler
  const manualSaveDraft = async () => {
    if (!isDirty || isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      const id = await saveDraft({
        name,
        description,
        technicalDetails,
        techKeywords,
        isBuilt,
        preferences
      }, draftId);
      if (id) setDraftId(id);
      toast.success('Draft saved ✓');
    } catch (e) {
      toast.error('Failed to save draft.');
      console.error(e);
    } finally {
      setIsSavingDraft(false);
    }
  };

  React.useEffect(() => {
    if (hasReachedProjectLimit) {
      setLimitModalOpen(true);
    }
  }, [hasReachedProjectLimit]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf') && !file.name.endsWith('.docx') && !file.name.endsWith('.doc')) {
      setError('Only PDF and DOCX files are supported');
      return;
    }

    setIsExtractingFile(true);
    setError(null);
    try {
      const response = await coreService.extractFromFile(file);
      if (response && response.success && response.data) {
        setName(response.data.name || name);
        setDescription(response.data.description || description);
        setTechnicalDetails(response.data.technicalDetails || technicalDetails);
        if (response.data.techKeywords && Array.isArray(response.data.techKeywords)) {
          setTechKeywords(response.data.techKeywords);
        }
      }
    } catch (err: any) {
      console.error('File extraction failed:', err);
      setError(err.response?.data?.message || 'Failed to extract project details from the file.');
    } finally {
      setIsExtractingFile(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const executeCreateProject = async (finalTechDetails: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        name,
        description,
        projectType,
        isBuilt: projectType === 'NON_TECH' ? false : isBuilt,
        preferences
      };

      if (projectType === 'TECH') {
        payload.technicalDetails = finalTechDetails;
        payload.techKeyword = techKeywords.length > 0 ? techKeywords.join(',') : '';
      }

      const result = await projectService.createProject(payload);
      if (draftId) {
        try {
          await deleteDraft(draftId);
        } catch (e) {
          console.error('Failed to delete draft after creating project', e);
        }
      }

      if (projectType === 'NON_TECH' || isBuilt) {
        navigate(`/projects/${result.data.id}`);
      } else {
        navigate(`/projects/${result.data.id}/generating`, {
          state: {
            project: result.data,
            formData: { description, technicalDetails: finalTechDetails, preferences }
          }
        });
      }
    } catch (err: any) {
      console.error('Project creation error:', err);
      setError(err.message || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasReachedProjectLimit) {
      setLimitModalOpen(true);
      return;
    }
    setError(null);
    setFormErrors({});

    if (!name.trim()) {
      setError('Please enter a project name');
      return;
    }

    if (visionWordCount < 500) {
      setError(`Vision statement requires at least 500 words. Currently provided: ${visionWordCount} words (${500 - visionWordCount} more needed).`);
      return;
    }

    if (projectType === 'NON_TECH') {
      await executeCreateProject('');
      return;
    }

    if (techKeywords.length === 0) {
      setShowKeywordsModal(true);
      return;
    }

    // Validate technical keywords before proceeding
    setIsValidatingKeywords(true);
    try {
      const resp = await coreService.validateKeywords(techKeywords);
      const validationResult = resp.data;
      const invalidKeywords = Object.entries(validationResult || {})
        .filter(([_, isValid]) => !isValid)
        .map(([key]) => key);

      if (invalidKeywords.length > 0) {
        setError(`Correct the keywords and then generate project. Non-technical terms found: ${invalidKeywords.join(', ')}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsValidatingKeywords(false);
        return;
      }
    } catch (err: any) {
      console.error('Keyword validation failed:', err);
      // Keep error message descriptive if it's a limit error
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset or upgrade.');
      } else {
        setError('Keyword validation failed. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsValidatingKeywords(false);
      return; // STOP EXECUTION
    } finally {
      setIsValidatingKeywords(false);
    }

    if (!technicalDetails.trim()) {
      setShowTechModal(true);
      return;
    }

    await executeCreateProject(technicalDetails);
  };

  const handleAutoGenerateKeywords = async () => {
    setShowKeywordsModal(false);
    setIsEnhancingKeywords(true);
    try {
      const result = await coreService.enhance({
        content: "Extracting keywords...",
        instruction: "Based on the project vision, identify the most likely or recommended TECHNICAL STACK. Return ONLY a comma-separated list of REAL technologies, programming languages, frameworks, databases, and architectural patterns (e.g., 'React, Node.js, PostgreSQL, Docker, Microservices'). IGNORE all business/marketing terms like 'social media marketing' or 'engagement'.",
        context: {
          projectDescription: description
        }
      });

      if (result.success && result.data.editedContent) {
        const keywords = result.data.editedContent.split(',').map((k: string) => k.trim()).filter((k: string) => k);
        setTechKeywords(keywords);
      }
    } catch (err: any) {
      console.error('AI Keyword Generation failed:', err);
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset.');
      } else {
        setError('AI Generation failed. Please enter keywords manually.');
      }
    } finally {
      setIsEnhancingKeywords(false);
    }
  };

  const handleAutoGenerate = async () => {
    setShowTechModal(false);
    setIsEnhancingTech(true);
    try {
      const result = await coreService.enhance({
        content: "Drafting technical stack...",
        instruction: "Generate a comprehensive, modern technology stack description based on this project description and keywords. Include Frontend, Backend, Database, and Key libraries.",
        context: {
          projectDescription: description,
          keywords: techKeywords.join(', ')
        }
      });

      if (result.success && result.data.editedContent) {
        setTechnicalDetails(result.data.editedContent);
      }
    } catch (err: any) {
      console.error('AI Generation failed:', err);
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset.');
      } else {
        setError('AI Generation failed. Please enter details manually.');
      }
    } finally {
      setIsEnhancingTech(false);
    }
  };

  const handleEnhanceKeywords = async () => {
    if (!description) return;
    setIsEnhancingKeywords(true);
    try {
      const result = await coreService.enhance({
        content: techKeywords.length > 0 ? techKeywords.join(', ') : "Generating tags...",
        instruction: "Provide a list of REAL software technologies, frameworks, and programming languages relevant to this project. Return EXCLUSIVELY a comma-separated list of specific tech terms (e.g. 'React, Python, AWS, Redis'). DO NOT include marketing objectives or business domain terms.",
        context: { projectDescription: description }
      });
      if (result.success && result.data.editedContent) {
        const keywords = result.data.editedContent.split(',').map((k: string) => k.trim()).filter((k: string) => k);
        setTechKeywords(keywords);
      }
    } catch (err: any) {
      console.error('Keyword enhancement failed:', err);
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset.');
      } else {
        setError('AI Enhancement failed. Please try again.');
      }
    } finally {
      setIsEnhancingKeywords(false);
    }
  };

  const handleProceedKeywordsEmpty = async () => {
    setShowKeywordsModal(false);
    if (!technicalDetails.trim()) {
      setShowTechModal(true);
    } else {
      await executeCreateProject(technicalDetails);
    }
  };

  const handleProceedEmpty = async () => {
    setShowTechModal(false);
    await executeCreateProject(technicalDetails);
  };

  const updatePreference = (key: keyof Preferences, field: 'enabled' | 'value', val: boolean | number) => {
    setPreferences(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: val }
    }));
  };

  const handleEnhanceDescription = async () => {
    if (!name) return;
    setIsEnhancingDesc(true);
    try {
      const result = await coreService.enhance({
        content: description || "Drafting description...",
        instruction: description
          ? `Enhance this project description for "${name}" to be more professional, architectural, clear, and detailed. Use a structured approach.`
          : `Generate a professional project description for a project named "${name}". Include an overview of the core problem it solves and its key features.`,
      });
      if (result.success && result.data.editedContent) {
        setDescription(result.data.editedContent);
      }
    } catch (err: any) {
      console.error('Enhancement failed:', err);
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset.');
      } else {
        setError('AI Enhancement failed. Please try again.');
      }
    } finally {
      setIsEnhancingDesc(false);
    }
  };

  const handleEnhanceTech = async () => {
    if (!description) return;
    setIsEnhancingTech(true);
    try {
      const result = await coreService.enhance({
        content: technicalDetails || "Drafting technical stack...",
        instruction: "Enhance these technical details to be more professional, technically precise, and comprehensive. Ensure it covers the full technical stack needs.",
        context: {
          projectDescription: description,
          keywords: techKeywords.join(', ')
        }
      });
      if (result.success && result.data.editedContent) {
        setTechnicalDetails(result.data.editedContent);
      }
    } catch (err: any) {
      console.error('Enhancement failed:', err);
      if (err.response?.status === 429) {
        setError('Daily AI token limit reached. Please wait for reset.');
      } else {
        setError('AI Enhancement failed. Please try again.');
      }
    } finally {
      setIsEnhancingTech(false);
    }
  };


  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 selection:bg-primary/30">
      <LoadingOverlay 
        isOpen={isEnhancingDesc || isEnhancingKeywords || isEnhancingTech} 
        message={
          isEnhancingDesc ? "Enhancing project description with AI..." :
          isEnhancingKeywords ? "Synthesizing technology keywords with AI..." :
          "Enhancing technical architecture details with AI..."
        } 
      />
      <div className="flex justify-between items-center mb-10">
        <div>
          <h1 className="text-3xl font-black font-heading text-white uppercase tracking-tight">System Initialization</h1>
          <p className="text-text-secondary text-sm font-medium mt-1">Configure your product architecture and AI parameters.</p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              if (isDirty) {
                setPendingUrl('-1');
                setShowSaveDraftModal(true);
              } else {
                navigate(-1);
              }
            }}
            className="h-12 px-6 bg-white/5 hover:bg-white/10 text-text-secondary border-white/10 rounded-xl font-bold uppercase tracking-widest transition-all"
          >
            Abort
          </Button>

          {/* Save Draft button — only shown when form has content */}
          {isDirty && (
            <button
              type="button"
              onClick={manualSaveDraft}
              disabled={isSavingDraft}
              title={draftId ? 'Update draft' : 'Save as draft'}
              className="h-12 px-5 flex items-center gap-2 rounded-xl text-sm font-bold uppercase tracking-widest border transition-all duration-200
                text-orange-400 bg-orange-500/5 border-orange-500/20
                hover:bg-orange-500/10 hover:border-orange-500/40
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingDraft ? (
                <>
                  <span className="w-4 h-4 border-2 border-orange-400/40 border-t-orange-400 rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-4 h-4" />
                  {draftId ? 'Update Draft' : 'Save Draft'}
                </>
              )}
            </button>
          )}
          <Tooltip
              content={!isVisionReady ? `Vision Statement requires at least 500 words to generate a project. You have ${visionWordCount} word${visionWordCount === 1 ? '' : 's'} — ${500 - visionWordCount} more needed.` : ''}
              position="bottom"
              disabled={isVisionReady}
            >
              <span className="inline-block">
          <Button
            onClick={handleSubmit}
            className={`h-12 px-8 text-white border-none rounded-xl font-black uppercase tracking-widest transition-all group ${isVisionReady && !isSubmitting
                      ? 'bg-gradient-to-r from-primary to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-[0_0_20px_rgba(255,107,0,0.3)] hover:scale-[1.02] active:scale-[0.98]'
                      : 'bg-white/10 cursor-not-allowed opacity-40 pointer-events-none'
                  }`}
            disabled={isSubmitting || !isVisionReady}
            loading={isSubmitting}
          >
            <div className="flex items-center justify-center gap-2">
              <SparklesIcon className={`h-5 w-5 ${isSubmitting ? 'animate-spin' : 'group-hover:rotate-12 transition-transform'}`} />
              {isSubmitting ? 'Initializing...' : 'Create Project'}
            </div>
          </Button>
        </span>
      </Tooltip>

        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl relative mb-4 animate-in shake duration-500" role="alert">
            <span className="block sm:inline font-bold text-sm tracking-wide">{error}</span>
          </div>
        )}

        {/* Project Category Selection (Tech vs Non-Tech) */}
        <Card className="overflow-hidden border-primary/30 shadow-[0_0_20px_rgba(255,107,0,0.15)] rounded-2xl bg-white/[0.03] backdrop-blur-xl">
          <CardContent className="p-6">
            <h2 className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-4 flex items-center gap-2">
              <SparklesIcon className="w-4 h-4" />
              Select Project Type
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() => setProjectType('TECH')}
                className={`p-5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                  projectType === 'TECH'
                    ? 'border-primary bg-primary/10 shadow-[0_0_15px_rgba(255,107,0,0.2)]'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <Cpu className="w-6 h-6 text-primary" />
                  </div>
                  {projectType === 'TECH' && (
                    <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest bg-primary text-white rounded-full">
                      Selected
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <h3 className="text-base font-black text-white uppercase tracking-tight">Tech</h3>
                  <p className="text-xs text-text-secondary mt-1 font-medium leading-relaxed">
                    Software architecture, technology stack specifications & engineering requirements.
                  </p>
                </div>
              </div>

              <div
                onClick={() => setProjectType('NON_TECH')}
                className={`p-5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                  projectType === 'NON_TECH'
                    ? 'border-primary bg-primary/10 shadow-[0_0_15px_rgba(255,107,0,0.2)]'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <Bolt className="w-6 h-6 text-orange-400" />
                  </div>
                  {projectType === 'NON_TECH' && (
                    <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest bg-orange-500 text-white rounded-full">
                      Selected
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <h3 className="text-base font-black text-white uppercase tracking-tight">Non-Tech</h3>
                  <p className="text-xs text-text-secondary mt-1 font-medium leading-relaxed">
                    Business initiatives & market strategy. Requires a minimum 500-word Vision to run Market Intelligence directly.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {isValidatingKeywords && (
          <div className="bg-primary/5 border border-primary/20 text-primary px-4 py-3 rounded-xl relative flex items-center gap-3 animate-pulse shadow-[0_0_15px_rgba(255,107,0,0.1)] mb-4" role="alert">
            <ArrowPathIcon className="h-5 w-5 animate-spin text-primary" />
            <span className="font-black uppercase tracking-[0.2em] text-[10px]">Analyzing Technical Stack Viability...</span>
          </div>
        )}

        {/* Document Parsing (Tech Projects Only) */}
        {projectType === 'TECH' && (
          <Card className="overflow-hidden border-primary/20 shadow-[0_0_15px_rgba(255,107,0,0.1)] rounded-2xl bg-white/[0.02] backdrop-blur-xl group">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <DocumentTextIcon className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-white uppercase tracking-tight">Smart Import</h2>
                    <p className="text-sm text-text-secondary mt-1">Upload a PDF or DOCX file (e.g., meeting notes, transcripts) to auto-fill project details.</p>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
                    disabled={isExtractingFile}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="relative pointer-events-none bg-black/40 border-primary/30 text-primary hover:bg-black/50 hover:text-white transition-colors uppercase font-bold tracking-widest px-6"
                    disabled={isExtractingFile}
                  >
                    {isExtractingFile ? (
                      <span className="flex items-center gap-2"><SparklesIcon className="w-4 h-4 animate-spin" /> Extracting...</span>
                    ) : (
                      "Select Document"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Basic Information */}
        <Card className="overflow-hidden border-white/10 shadow-2xl shadow-black/20 rounded-2xl bg-[#131B2C]/40 backdrop-blur-xl group">
          <div
            className="flex justify-between items-center p-6 cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
            onClick={() => setShowBasicInfo(!showBasicInfo)}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                <InformationCircleIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-black text-white uppercase tracking-tight">
                  {projectType === 'NON_TECH' ? 'Business Vision' : 'Core Architecture'}
                </h2>
                <p className="text-xs text-text-secondary font-medium mt-0.5">
                  {projectType === 'NON_TECH'
                    ? 'Define the strategic business vision (minimum 500 words required).'
                    : 'Define the fundamental objective and ecosystem of your application.'}
                </p>
              </div>
            </div>
            {showBasicInfo ? <ChevronUpIcon className="h-5 w-5 text-text-secondary/50" /> : <ChevronDownIcon className="h-5 w-5 text-text-secondary/50" />}
          </div>

          {showBasicInfo && (
            <CardContent className="border-t border-white/5 bg-transparent p-6 space-y-8">
              <div className="grid grid-cols-1 gap-8">
                <Input
                  label={<span>Project Name <span className="text-primary">*</span></span>}
                  id="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined }));
                  }}
                  error={formErrors.name}
                  placeholder="e.g. Market Expansion Strategy 2026"
                />

                <div className="group/field flex flex-col gap-1">
                  {/* Label row with word count + button */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-white uppercase tracking-tight">
                      Vision Statement <span className="text-primary">*</span>
                    </span>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-semibold tabular-nums px-3 py-1 rounded-lg border border-white/10 bg-white/5 ${
                        visionWordCount >= 500 ? 'text-green-400 border-green-500/30 bg-green-500/10' : 'text-text-secondary/60'
                      }`}>
                        {visionWordCount} / 500 words min
                      </span>
                      <Tooltip
                        content={
                          !name
                            ? 'Enter project name first'
                            : description.trim().split(/\s+/).filter(Boolean).length < 500
                            ? 'Enter at least 500 words to enable Smarter Vision'
                            : 'Synthesize Vision with AI'
                        }
                        disabled={false}
                      >
                        <span className="inline-block">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleEnhanceDescription}
                            disabled={isEnhancingDesc || !name || !isVisionReady}
                            className="h-8 text-[9px] flex items-center gap-2 border-primary/20 text-primary bg-primary/5 hover:bg-primary/10 transition-all font-black uppercase tracking-[0.15em] rounded-xl shadow-[0_0_10px_rgba(255,107,0,0.1)] disabled:opacity-30"
                          >
                            <SparklesIcon className={`h-3.5 w-3.5 ${isEnhancingDesc ? 'animate-spin' : ''}`} />
                            {isEnhancingDesc ? 'Synthesizing...' : 'Smarter Vision'}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (formErrors.description) setFormErrors(prev => ({ ...prev, description: undefined }));
                    }}
                    error={formErrors.description}
                    placeholder={
                      projectType === 'NON_TECH'
                        ? "Describe the business strategy, market opportunity, target audience, competitive advantages, and long-term vision... (Minimum 500 words required for Non-Tech projects)"
                        : "Describe the problem space and architectural goal... (minimum 500 words to unlock Smarter Vision)"
                    }
                    rows={projectType === 'NON_TECH' ? 14 : 10}
                  />
                </div>

                {projectType === 'TECH' && (
                  <>
                    <div className="group/field flex flex-col gap-1">
                      {/* Label row with button */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white uppercase tracking-tight">
                            Technology Stack <span className="text-text-secondary/40 font-bold">(Optional Tags)</span>
                          </span>
                          <Tooltip content="Specify languages, frameworks, or infrastructure components.">
                            <InformationCircleIcon className="h-4 w-4 text-text-secondary/40 hover:text-primary cursor-help" />
                          </Tooltip>
                        </div>
                        <Tooltip content={description ? 'Extract Stack with AI' : 'Enter vision first'} disabled={false}>
                          <span className="inline-block">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={handleEnhanceKeywords}
                              disabled={isEnhancingKeywords || !description}
                              className="h-8 text-[9px] flex items-center gap-2 border-primary/20 text-primary bg-primary/5 hover:bg-primary/10 transition-all font-black uppercase tracking-[0.15em] rounded-xl shadow-[0_0_10px_rgba(255,107,0,0.1)] disabled:opacity-30"
                            >
                              <SparklesIcon className={`h-3.5 w-3.5 ${isEnhancingKeywords ? 'animate-spin' : ''}`} />
                              {isEnhancingKeywords ? 'Extracting...' : 'Auto-Stack'}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                      <TagInput
                        tags={techKeywords}
                        onChange={(newTags) => {
                          setTechKeywords(newTags);
                          if (formErrors.techKeyword && newTags.length > 0) {
                            setFormErrors(prev => ({ ...prev, techKeyword: undefined }));
                          }
                        }}
                        error={formErrors.techKeyword}
                        placeholder="e.g. React, PostgreSQL, Docker, AWS..."
                      />
                    </div>

                    <div className="group/field flex flex-col gap-1">
                      {/* Label row with button */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white uppercase tracking-tight">
                            Technical Specifications <span className="text-text-secondary/40 font-bold">(Optional)</span>
                          </span>
                          <Tooltip content="Leave blank to let DocSprintAI architect the technical solution based on your vision.">
                            <InformationCircleIcon className="h-4 w-4 text-text-secondary/40 hover:text-primary cursor-help" />
                          </Tooltip>
                        </div>
                        <Tooltip content={description ? 'Draft Technical Blueprint' : 'Enter vision first'} disabled={false}>
                          <span className="inline-block">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={handleEnhanceTech}
                              disabled={isEnhancingTech || !description}
                              className="h-8 text-[9px] flex items-center gap-2 border-primary/20 text-primary bg-primary/5 hover:bg-primary/10 transition-all font-black uppercase tracking-[0.15em] rounded-xl shadow-[0_0_10px_rgba(255,107,0,0.1)] disabled:opacity-30"
                            >
                              <SparklesIcon className={`h-3.5 w-3.5 ${isEnhancingTech ? 'animate-spin' : ''}`} />
                              {isEnhancingTech ? 'Architecting...' : 'Expert Blueprint'}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                      <Textarea
                        id="technicalDetails"
                        value={technicalDetails}
                        onChange={(e) => {
                          setTechnicalDetails(e.target.value);
                          if (formErrors.technicalDetails) setFormErrors(prev => ({ ...prev, technicalDetails: undefined }));
                        }}
                        error={formErrors.technicalDetails}
                        placeholder="Provide specific technical constraints or architectural patterns..."
                        rows={6}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#0B0F19] rounded-lg border border-white/5">
                          <FolderOpen className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-white uppercase tracking-tight">Existing Repository?</label>
                          <p className="text-xs text-text-secondary font-medium">Toggle if you are generating requirements for a built project.</p>
                        </div>
                      </div>
                      <Switch checked={isBuilt} onChange={setIsBuilt} />
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Preferences (Tech Projects Only) */}
        {projectType === 'TECH' && !isBuilt && (
          <Card className="overflow-hidden border-white/10 shadow-2xl shadow-black/20 rounded-2xl bg-[#131B2C]/40 backdrop-blur-xl group">
            <div
              className="flex justify-between items-center p-6 cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
              onClick={() => setShowPreferences(!showPreferences)}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                  <Bolt className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-white uppercase tracking-tight">AI Generation Parameters</h2>
                  <p className="text-xs text-text-secondary font-medium mt-0.5">Control the density and complexity of AI-generated documentation.</p>
                </div>
              </div>
              {showPreferences ? <ChevronUpIcon className="h-5 w-5 text-text-secondary/50" /> : <ChevronDownIcon className="h-5 w-5 text-text-secondary/50" />}
            </div>

            {showPreferences && (
              <CardContent className="border-t border-white/5 bg-transparent p-6 space-y-10">
                {[
                  { key: 'brd', label: 'Business Requirements', desc: 'Threshold for core business logic units' },
                  { key: 'prd', label: 'Product Requirements', desc: 'Threshold for functional specification units' },
                  { key: 'uir', label: 'UI Specifications', desc: 'Threshold for visual interface components' },
                  { key: 'nfr', label: 'Non-Functional Constraints', desc: 'Threshold for security and performance units' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center space-x-8">
                    <div className="w-1/3">
                      <label className="block text-xs font-black text-white uppercase tracking-[0.1em]">{item.label}</label>
                      <p className="text-[10px] text-text-secondary font-bold uppercase tracking-wider mt-1 opacity-60">{item.desc}</p>
                    </div>

                    <div className="flex items-center space-x-6 flex-1">
                      <Switch
                        checked={preferences[item.key as keyof Preferences].enabled}
                        onChange={(val) => updatePreference(item.key as keyof Preferences, 'enabled', val)}
                      />
                      <div className="flex-1 px-2">
                        <Slider
                          value={preferences[item.key as keyof Preferences].value}
                          min={0}
                          max={30}
                          onChange={(val) => updatePreference(item.key as keyof Preferences, 'value', val)}
                          disabled={!preferences[item.key as keyof Preferences].enabled}
                        />
                      </div>
                      <span className="text-sm font-black text-primary w-10 text-right font-mono">
                        {preferences[item.key as keyof Preferences].value.toString().padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* MCP Integrations (Tech Projects Only) */}
        {projectType === 'TECH' && (
          <Card className="overflow-hidden border-white/10 shadow-2xl shadow-black/20 rounded-2xl bg-[#131B2C]/40 backdrop-blur-xl group">
            <div
              className="flex justify-between items-center p-6 cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
              onClick={() => setShowMCP(!showMCP)}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                  <Cpu className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-white uppercase tracking-tight">MCP Protocol Bridge</h2>
                  <p className="text-xs text-text-secondary font-medium mt-0.5">Connect external Model Context Protocol servers for specialized intelligence.</p>
                </div>
              </div>
              {showMCP ? <ChevronUpIcon className="h-5 w-5 text-text-secondary/50" /> : <ChevronDownIcon className="h-5 w-5 text-text-secondary/50" />}
            </div>

          {showMCP && (
            <CardContent className="border-t border-white/5 bg-transparent p-6">
              <div className="flex flex-wrap gap-4">
                {/* Mock Card 1 */}
                <div className="group/mcp border border-white/5 rounded-2xl p-6 flex flex-col items-center justify-center w-48 h-36 hover:border-primary/40 hover:bg-primary/[0.03] hover:shadow-[0_0_20px_rgba(255,107,0,0.1)] cursor-pointer transition-all bg-white/[0.02] relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2 opacity-0 group-hover/mcp:opacity-100 transition-opacity">
                    <CheckCircle className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-white font-black mb-3 text-2xl group-hover/mcp:scale-110 transition-transform bg-gradient-to-br from-white to-text-secondary bg-clip-text text-transparent opacity-80 group-hover/mcp:opacity-100 italic">aws</span>
                  <span className="text-[10px] text-center font-black text-text-secondary uppercase tracking-widest group-hover/mcp:text-primary transition-colors">Bedrock KB</span>
                </div>

                {/* Mock Card 2 */}
                <div className="group/mcp border border-white/5 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center w-48 h-36 hover:border-primary/40 hover:bg-primary/[0.03] cursor-pointer transition-all bg-transparent">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover/mcp:scale-110 transition-all border border-white/5">
                    <Plus className="w-6 h-6 text-text-secondary group-hover/mcp:text-primary transition-colors" />
                  </div>
                  <span className="text-[10px] text-center font-black text-text-secondary uppercase tracking-widest group-hover/mcp:text-text-secondary/80 transition-colors">Add Protocol</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
        )}
      </form>


      <ConfirmationModal
        isOpen={limitModalOpen}
        onClose={() => navigate('/dashboard')}
        onConfirm={() => navigate('/payment')}
        title="Project Limit Reached"
        message="Your project limit is reached. Upgrade your plan to create more projects and unlock premium features!"
        confirmLabel="Yes, Upgrade"
        cancelLabel="No, Dashboard"
        variant="info"
      />

      {/* Tech Keywords Missing Modal */}
      <ConfirmationModal
        isOpen={showKeywordsModal}
        onClose={handleProceedKeywordsEmpty}
        onConfirm={handleAutoGenerateKeywords}
        title="Technical Keywords Missing"
        message="You haven't specified any technical keywords or tags. Would you like our AI to automatically generate them based on your project description?"
        confirmLabel="Yes, Generate Tags"
        cancelLabel="No, I'll Skip"
        variant="info"
      />

      {/* Tech Details Missing Modal */}
      <ConfirmationModal
        isOpen={showTechModal}
        onClose={handleProceedEmpty}
        onConfirm={handleAutoGenerate}
        title="Technical Details Missing"
        message="You haven't specified a technical stack description. Would you like our AI to automatically draft a recommended stack for you?"
        confirmLabel="Yes, Generate Description"
        cancelLabel="No, I'll Skip"
        variant="info"
      />

      <SaveDraftModal
        isOpen={showSaveDraftModal}
        onClose={() => setShowSaveDraftModal(false)}
        onSave={handleSaveDraft}
        onDiscard={handleDiscardDraft}
      />
    </div >
  );
};
